import axios, { AxiosError, isAxiosError, Method } from 'axios';
import { useApiServices } from '../providers/useApiServices';
import {
  Endpoint,
  EndpointParameters,
  ErrorStatusCode,
  Fetcher,
  SafeApiResponse,
  TypedApiResponse,
  TypedErrorResponse,
  TypedHeaders,
} from '../rest-api/generated/laravel-typed-openapi';
import { applyDeclaredHeaders, applyLanguageHeader, buildRequestBody, isFormDataBody } from './rest-request';

type FetcherInput = {
  method: Method;
  url: URL;
  urlSearchParams?: URLSearchParams | undefined;
  parameters?: EndpointParameters | undefined;
  path: string;
  overrides?: RequestInit;
  throwOnStatusError?: boolean;
};

const isFormData = isFormDataBody;
const createBody = buildRequestBody;

export const useRestApiFetcher = (): Fetcher['fetch'] => {
  const { getToken } = useApiServices();

  return async (input: FetcherInput): Promise<SafeApiResponse<Endpoint>> => {
    const token = await getToken({ template: 'one_hour_token' });
    try {
      const response = await axios<SafeApiResponse<Endpoint>>(input.url.href, {
        method: input.method as Method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      // if (response.status >= 400 && input.throwOnStatusError) {
      //   throw new Error(response.data.errors[0].message);
      // }

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const serverError = error as AxiosError<
          TypedErrorResponse<TypedApiResponse, ErrorStatusCode, TypedHeaders<unknown>>
        >;
        if (serverError && serverError.response) {
          const errorMessage = serverError.response.data.data || 'Error in API request';
          throw new Error(errorMessage);
        }
      }
      throw error;
    }
  };
};

export const useDefaultRestApiFetcher = (): Fetcher['fetch'] => {
  const { getToken } = useApiServices();

  return async (input: FetcherInput) => {
    const headers = new Headers();

    // Handle query parameters
    if (input.urlSearchParams) {
      input.url.search = input.urlSearchParams.toString();
    }

    // Handle request body for mutation methods

    const body = createBody(input, headers);

    if (!isFormData(input) && body) {
      headers.set('Content-Type', 'application/json');
    }

    headers.set('Authorization', `Bearer ${await getToken({ template: 'one_hour_token' })}`);

    applyLanguageHeader(headers);

    // Add custom headers
    applyDeclaredHeaders(input, headers);

    const response = await fetch(input.url, {
      method: input.method.toUpperCase(),

      ...(body && { body }),
      headers,
      ...input.overrides,
    });

    return response;
  };
};

/**
 * A fetcher that carries a bearer token it was handed, rather than one it resolves from Clerk.
 *
 * Sign-out cleanup is the reason it exists. Clerk's native account view signs the user out itself
 * and only tells the app afterwards, so `getToken()` returns nothing from the moment the cleanup
 * needs it — see `useRetainedAuthToken`. A request that must outlive the session cannot go through
 * `useDefaultRestApiFetcher`, whose whole job is to ask the session that has just ended.
 *
 * Deliberately not a hook: the caller is a plain function running after its React tree has
 * unmounted. Build a one-off client with it — `createApiClient({ fetch: ... }, baseUrl)` — rather
 * than reaching for the provider's.
 */
export const createStaticTokenRestApiFetcher = (authToken: string): Fetcher['fetch'] => {
  return async (input) => {
    const headers = new Headers();

    if (input.urlSearchParams) {
      input.url.search = input.urlSearchParams.toString();
    }

    const body = createBody(input, headers);

    if (!isFormData(input) && body) {
      headers.set('Content-Type', 'application/json');
    }

    headers.set('Authorization', `Bearer ${authToken}`);

    applyLanguageHeader(headers);

    applyDeclaredHeaders(input, headers);

    return await fetch(input.url, {
      method: input.method.toUpperCase(),
      ...(body && { body }),
      headers,
      ...input.overrides,
    });
  };
};
